/**
 * @module UseHypotheses
 * @purpose Hook for fetching and triggering Stage-6 hypothesis evaluation for a reasoning run.
 * @depends services/reasoning (listHypotheses, evaluateHypotheses), types/reasoning
 * @dependedOnBy pages/case/analysis/reasoning/HypothesesPanel
 *
 * @rules
 * - Performance: stale-request requestIdRef guard prevents out-of-order state writes on
 *     rapid caseId/runId changes; evaluate() refreshes the list on success only
 * - State: local useState per hook; no shared context
 * - Errors: error shape is string | null; ApiError.message extracted in catch blocks;
 *     evaluate errors are surfaced in the same error field as list-fetch errors
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { evaluateHypotheses, listHypotheses } from '../services/reasoning'
import type { Hypothesis, SignalCounts } from '../types/reasoning'

// =============================================================================
// Module-level helpers
// =============================================================================

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

// =============================================================================
// useHypotheses
// =============================================================================

export interface UseHypothesesResult {
  hypotheses: Hypothesis[]
  signalCounts: SignalCounts
  totalObservations: number
  loading: boolean
  error: string | null
  evaluating: boolean
  evaluate: (opts?: { force?: boolean }) => Promise<void>
  refetch: () => Promise<void>
}

/**
 * Fetch and evaluate Stage-6 hypotheses for a reasoning run.
 *
 * @behaviour Fetches the hypothesis list on mount and when caseId/runId change.
 *   Skips and resets all state when either param is undefined.
 *   Stale-request guard (requestIdRef) prevents out-of-order state writes when
 *   caseId/runId changes quickly.
 *   evaluate() triggers POST stage6-evaluate (backend chooses predicate/version defaults)
 *   then refreshes the list on success. Sets evaluating:true while the POST is in flight.
 * @errors Sets error string for all failures; clears on next fetch or evaluate attempt.
 */
export function useHypotheses(
  caseId: string | undefined,
  runId: string | undefined,
): UseHypothesesResult {
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([])
  const [signalCounts, setSignalCounts] = useState<SignalCounts>({})
  const [totalObservations, setTotalObservations] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [evaluating, setEvaluating] = useState(false)

  const requestIdRef = useRef(0)

  const fetch = useCallback(async () => {
    if (!caseId || !runId) {
      setHypotheses([])
      setSignalCounts({})
      setTotalObservations(0)
      setError(null)
      setLoading(false)
      return
    }

    requestIdRef.current += 1
    const thisId = requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      const data = await listHypotheses(caseId, runId)
      if (thisId !== requestIdRef.current) return
      setHypotheses(data.hypotheses)
      setSignalCounts(data.signalCounts)
      setTotalObservations(data.totalObservations)
    } catch (err) {
      if (thisId !== requestIdRef.current) return
      setError(messageOf(err, 'Failed to fetch hypotheses'))
      setHypotheses([])
      setSignalCounts({})
      setTotalObservations(0)
    } finally {
      if (thisId === requestIdRef.current) setLoading(false)
    }
  }, [caseId, runId])

  const evaluate = useCallback(async (opts?: { force?: boolean }) => {
    if (!caseId || !runId) return

    setEvaluating(true)
    setError(null)

    try {
      await evaluateHypotheses(caseId, runId, opts ?? {})
      await fetch()
    } catch (err) {
      setError(messageOf(err, 'Failed to evaluate hypotheses'))
    } finally {
      setEvaluating(false)
    }
  }, [caseId, runId, fetch])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return {
    hypotheses,
    signalCounts,
    totalObservations,
    loading,
    error,
    evaluating,
    evaluate,
    refetch: fetch,
  }
}
